import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';
import { pmMembersService, getDisplayName, type ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { tasksService, type Task, type TaskInput } from '../../../services/projectManagement/tasksService';
import PmSavedViewsControl from '../components/PmSavedViewsControl';
import PmFilterBuilder from '../components/PmFilterBuilder';
import TaskDetailModal from '../components/TaskDetailModal';
import { applyPmFilters, normalizePmFilterState, type PmFilterState } from '../lib/pmFilters';
import type { SortDirection, SortField } from '../components/TaskTableView';

type TaskStatus = 'backlog' | 'todo' | 'in-progress' | 'review' | 'done' | 'blocked';

type BulkAssigneeSelection = '' | '__unassigned__' | string;

function isInteractiveElement(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = String(el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

const formatDate = (date: string | null | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString() : '-';
};

const sortTasks = (items: Task[], sortField: SortField, sortDirection: SortDirection): Task[] => {
  return [...(items || [])].sort((a, b) => {
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (sortField === 'due_date') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDirection === 'asc' ? comparison : -comparison;
  });
};

const MyTasksTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<ProjectManagementMember[]>([]);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [lastSelectedTaskId, setLastSelectedTaskId] = useState<string | null>(null);

  const [taskFilters, setTaskFilters] = useState<PmFilterState>({ query: '', combinator: 'and', rules: [] });
  const [tableSortField, setTableSortField] = useState<SortField>('due_date');
  const [tableSortDirection, setTableSortDirection] = useState<SortDirection>('asc');

  const [bulkStatus, setBulkStatus] = useState<TaskStatus | ''>('');
  const [bulkDueDate, setBulkDueDate] = useState<string>('');
  const [bulkAssignee, setBulkAssignee] = useState<BulkAssigneeSelection>('');

  const fetchData = useCallback(async () => {
    if (!activeOrgId || !user?.id) return;
    setLoading(true);
    try {
      const [tasksRes, projectsRes, membersRes] = await Promise.all([
        tasksService.list({ organization_id: activeOrgId, assignee_id: user.id }),
        projectsService.list({ organization_id: activeOrgId }),
        pmMembersService.list(activeOrgId),
      ]);

      if (tasksRes.error) {
        toast.error('Failed to load tasks');
        setTasks([]);
      } else {
        setTasks(tasksRes.data || []);
      }

      if (projectsRes.error) {
        setProjects([]);
      } else {
        setProjects(projectsRes.data || []);
      }

      if (membersRes.error) {
        setMembers([]);
      } else {
        setMembers(membersRes.data || []);
      }
    } catch {
      toast.error('Failed to load data');
      setTasks([]);
      setProjects([]);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, user?.id]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const projectOptions = useMemo(() => {
    return (projects || []).map(p => ({ value: p.id, label: p.name }));
  }, [projects]);

  const taskLabelOptions = useMemo(() => {
    const s = new Set<string>();
    tasks.forEach(t => (t.labels || []).forEach(l => s.add(String(l))));
    return Array.from(s)
      .filter(Boolean)
      .sort()
      .map(l => ({ value: l, label: l }));
  }, [tasks]);

  const taskFilterFields = useMemo(() => {
    return [
      {
        field: 'status',
        label: 'Status',
        type: 'select' as const,
        options: [
          { value: 'backlog', label: 'Backlog' },
          { value: 'todo', label: 'To Do' },
          { value: 'in-progress', label: 'In Progress' },
          { value: 'review', label: 'Review' },
          { value: 'done', label: 'Done' },
          { value: 'blocked', label: 'Blocked' },
        ],
      },
      {
        field: 'priority',
        label: 'Priority',
        type: 'select' as const,
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'urgent', label: 'Urgent' },
        ],
      },
      {
        field: 'project_id',
        label: 'Project',
        type: 'select' as const,
        options: projectOptions,
      },
      {
        field: 'due_date',
        label: 'Due Date',
        type: 'date' as const,
      },
      {
        field: 'estimated_time',
        label: 'Estimate (hours)',
        type: 'number' as const,
      },
      {
        field: 'labels',
        label: 'Labels',
        type: 'select' as const,
        options: taskLabelOptions,
      },
    ];
  }, [projectOptions, taskLabelOptions]);

  const visibleTasks = useMemo(() => {
    return applyPmFilters(tasks, taskFilters, ['title', 'description', 'project_name']);
  }, [tasks, taskFilters]);

  const sortedTasks = useMemo(() => {
    return sortTasks(visibleTasks, tableSortField, tableSortDirection);
  }, [visibleTasks, tableSortField, tableSortDirection]);

  const selectedTaskIdList = useMemo(() => Array.from(selectedTaskIds), [selectedTaskIds]);

  const clearSelection = useCallback(() => {
    setSelectedTaskIds(new Set());
    setLastSelectedTaskId(null);
  }, []);

  const toggleTaskSelection = useCallback((taskId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
    setLastSelectedTaskId(taskId);
  }, []);

  const toggleSelectAll = useCallback(() => {
    const allVisibleIds = sortedTasks.map(t => t.id);
    if (allVisibleIds.length === 0) return;

    setSelectedTaskIds((prev) => {
      if (prev.size === allVisibleIds.length) {
        setLastSelectedTaskId(null);
        return new Set();
      }
      setLastSelectedTaskId(allVisibleIds[0] || null);
      return new Set(allVisibleIds);
    });
  }, [sortedTasks]);

  const runBulkUpdate = useCallback(async (updates: Partial<TaskInput>) => {
    if (!activeOrgId) return;
    const ids = selectedTaskIdList;
    if (ids.length === 0) return;

    const { error } = await tasksService.bulkUpdate(ids, activeOrgId, updates);
    if (error) {
      toast.error('Failed to update tasks');
      return;
    }

    toast.success('Tasks updated');
    clearSelection();
    setBulkStatus('');
    setBulkDueDate('');
    setBulkAssignee('');
    await fetchData();
  }, [activeOrgId, selectedTaskIdList, clearSelection, fetchData]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isInteractiveElement(e.target)) return;

      if (e.key === 'Escape' && selectedTaskIds.size > 0) {
        e.preventDefault();
        clearSelection();
        return;
      }

      if (e.key === 'Enter' && selectedTaskIds.size > 0) {
        e.preventDefault();
        const candidate = (lastSelectedTaskId && selectedTaskIds.has(lastSelectedTaskId))
          ? lastSelectedTaskId
          : Array.from(selectedTaskIds)[0];
        if (candidate) {
          setSelectedTaskId(candidate);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [clearSelection, lastSelectedTaskId, selectedTaskIds]);

  const assigneeOptions = useMemo(() => {
    const opts = (members || []).map(m => ({ value: m.user_id, label: getDisplayName(m) }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [members]);

  const applySavedViewPayload = useCallback((payload: Record<string, any>) => {
    const nextTaskFilters = normalizePmFilterState((payload as any)?.taskFilters);
    const nextTableSortField = String((payload as any)?.tableSortField || '');
    const nextTableSortDirection = String((payload as any)?.tableSortDirection || '');

    setTaskFilters(nextTaskFilters);

    if (['title', 'status', 'priority', 'due_date', 'assignee_id', 'project_name', 'estimated_time'].includes(nextTableSortField)) {
      setTableSortField(nextTableSortField as SortField);
    }

    if (['asc', 'desc'].includes(nextTableSortDirection)) {
      setTableSortDirection(nextTableSortDirection as SortDirection);
    }
  }, []);

  if (!activeOrgId) {
    return <div className="rounded-xl bg-white p-8">No organization selected</div>;
  }

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading tasks...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <div className="text-lg font-semibold text-gray-900">My Tasks</div>
          <div className="text-sm text-gray-600">{visibleTasks.length} task{visibleTasks.length !== 1 ? 's' : ''}</div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Sort:</label>
            <select
              value={tableSortField}
              onChange={(e) => setTableSortField(e.target.value as SortField)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="due_date">Due Date</option>
              <option value="priority">Priority</option>
              <option value="status">Status</option>
              <option value="project_name">Project</option>
              <option value="title">Title</option>
            </select>
            <button
              onClick={() => setTableSortDirection(tableSortDirection === 'asc' ? 'desc' : 'asc')}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {tableSortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <PmSavedViewsControl
            organizationId={activeOrgId}
            scope="tasks"
            getCurrentPayload={() => ({
              viewMode: 'table',
              taskFilters,
              tableSortField,
              tableSortDirection,
            })}
            applyPayload={applySavedViewPayload}
          />
        </div>
      </div>

      <PmFilterBuilder
        title="My Tasks"
        placeholder="Search your tasks..."
        value={taskFilters}
        onChange={setTaskFilters}
        availableFields={taskFilterFields}
      />

      {selectedTaskIds.size > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-sm font-medium text-blue-900">
            {selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? 's' : ''} selected
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value as any)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Status…</option>
              <option value="backlog">Backlog</option>
              <option value="todo">To Do</option>
              <option value="in-progress">In Progress</option>
              <option value="review">Review</option>
              <option value="done">Done</option>
              <option value="blocked">Blocked</option>
            </select>
            <button
              onClick={() => {
                if (!bulkStatus) return;
                void runBulkUpdate({ status: bulkStatus as any });
              }}
              className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
              disabled={!bulkStatus}
            >
              Apply
            </button>

            <input
              type="date"
              value={bulkDueDate}
              onChange={(e) => setBulkDueDate(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            />
            <button
              onClick={() => {
                if (!bulkDueDate) return;
                void runBulkUpdate({ due_date: bulkDueDate } as any);
              }}
              className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
              disabled={!bulkDueDate}
            >
              Set Due
            </button>
            <button
              onClick={() => void runBulkUpdate({ due_date: null } as any)}
              className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
            >
              Clear Due
            </button>

            <select
              value={bulkAssignee}
              onChange={(e) => setBulkAssignee(e.target.value as BulkAssigneeSelection)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Assignee…</option>
              <option value="__unassigned__">Unassigned</option>
              {assigneeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={() => {
                if (!bulkAssignee) return;
                const nextAssignee = bulkAssignee === '__unassigned__' ? null : bulkAssignee;
                void runBulkUpdate({ assignee_id: nextAssignee } as any);
              }}
              className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
              disabled={!bulkAssignee}
            >
              Reassign
            </button>

            <button
              onClick={clearSelection}
              className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedTaskIds.size > 0 && selectedTaskIds.size === sortedTasks.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Task</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Project</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {sortedTasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.has(task.id)}
                      onChange={() => toggleTaskSelection(task.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedTaskId(task.id)}
                      className="text-left"
                    >
                      <div className="font-medium text-gray-900 hover:text-blue-600">
                        {task.title}
                      </div>
                      {task.description && (
                        <div className="mt-1 text-sm text-gray-500 truncate max-w-md">{task.description}</div>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{task.project_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{task.status}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{task.priority}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatDate(task.due_date)}</td>
                </tr>
              ))}

              {sortedTasks.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500">
                    No tasks to display
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedTaskId && (
        <TaskDetailModal
          taskId={selectedTaskId}
          organizationId={activeOrgId}
          onClose={() => setSelectedTaskId(null)}
          onUpdate={() => {
            void fetchData();
          }}
        />
      )}
    </div>
  );
};

export default MyTasksTab;
